import React, { useState, useEffect } from "react"
import {
    Modal,
    ModalBody,
    ModalButtons,
    ModalContent,
    ModalHeader,
    ModalHeaderTitle,
    ModalSize,
} from "@ui/common/Modal"
import { Grid } from "@ui/common/grids/Grid"
import { FormField, FormFieldNote, FormFieldNoteType } from "@ui/common/form/FormField"
import { TextInputDateTimePicker } from "@ui/common/form/DatePicker"
import { Button, ButtonType, ButtonTheme } from "@ui/common/buttons/Button"
import { ServiceItemRequiredFieldKey } from "@model/company/CompanySettings"
import { labelsForServiceItemRequiredFields } from "@model/company/WorkOrderRequiredFieldsUtil"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { ImageUrls } from "@ui/common/images/ImageUrls"
import { WorkRequestStatus } from "@model/workRequests/WorkRequest"

type Props = {
    isVisible: boolean
    onClose: () => void
    onConfirm: (completionDate: Date, items: ServiceItemForm[]) => void
    serviceItems: ServiceItemForm[]
    /**
     * Missing required fields per Service Item, keyed by uiElementId. A listed item cannot be checked
     * off here, which keeps the confirm button disabled until the fields are filled in on the item.
     */
    missingFieldsByItem?: Record<number, ServiceItemRequiredFieldKey[]>
}

type BaseProps = Props & {
    title: string
    confirmLabel: string
    initialCompletionDate?: Date | null
    // Extra form fields rendered between the Completion Date and the Service Items review.
    // additionalFieldsValid gates the confirm button together with the base validations.
    additionalFields?: React.ReactNode
    additionalFieldsValid?: boolean
}

export function CompleteWorkOrderModal({
    isVisible,
    onClose,
    onConfirm,
    serviceItems,
    missingFieldsByItem,
}: Props): JSX.Element {
    return (
        <WorkOrderServiceItemsReviewModal
            isVisible={isVisible}
            onClose={onClose}
            onConfirm={onConfirm}
            serviceItems={serviceItems}
            missingFieldsByItem={missingFieldsByItem}
            title="Complete Work Order"
            confirmLabel="Complete Work Order"
        />
    )
}

export function WorkOrderServiceItemsReviewModal({
    isVisible,
    onClose,
    onConfirm,
    serviceItems,
    missingFieldsByItem = {},
    title,
    confirmLabel,
    initialCompletionDate,
    additionalFields,
    additionalFieldsValid = true,
}: BaseProps): JSX.Element {
    const missingFieldsFor = (id: number): ServiceItemRequiredFieldKey[] => missingFieldsByItem[id] ?? []
    const canComplete = (id: number): boolean => missingFieldsFor(id).length === 0

    const [completionDate, setCompletionDate] = useState<Date>(initialCompletionDate ?? new Date())
    const [itemsStatus, setItemsStatus] = useState<Record<number, WorkRequestStatus>>({})

    useEffect(() => {
        if (isVisible) setCompletionDate(initialCompletionDate ?? new Date())
    }, [isVisible])

    useEffect(() => {
        const initialStatus: Record<number, WorkRequestStatus> = {}
        serviceItems.forEach((item) => {
            initialStatus[item.uiElementId] = item.status
        })
        setItemsStatus(initialStatus)
    }, [serviceItems, isVisible])

    const toggleItem = (id: number) => {
        // Completing is blocked while fields are missing; un-completing an item that was already
        // Done before the requirement existed is still allowed.
        if (!canComplete(id) && itemsStatus[id] !== WorkRequestStatus.Done) return

        setItemsStatus((prev) => ({
            ...prev,
            [id]: prev[id] === WorkRequestStatus.Done ? WorkRequestStatus.ToDo : WorkRequestStatus.Done
        }))
    }

    const toggleAllItems = () => {
        const allCompleted = serviceItems.every((item) => itemsStatus[item.uiElementId] === WorkRequestStatus.Done)
        const newStatus: Record<number, WorkRequestStatus> = {}

        serviceItems.forEach((item) => {
            // An item already completed before the company added a requirement keeps its status:
            // Check All must not quietly un-complete work that was finished under the old rules.
            const alreadyDone = itemsStatus[item.uiElementId] === WorkRequestStatus.Done
            const completing = !allCompleted && (canComplete(item.uiElementId) || alreadyDone)
            newStatus[item.uiElementId] = completing ? WorkRequestStatus.Done : WorkRequestStatus.ToDo
        })

        setItemsStatus(newStatus)
    }

    const allCompleted =
        serviceItems.length > 0 &&
        serviceItems.every((item) => itemsStatus[item.uiElementId] === WorkRequestStatus.Done)

    function dateIsValid(): boolean | undefined {
        return (completionDate && completionDate < new Date())
    }

    return (
        <Modal isVisible={isVisible} useContentTag={true} onClose={onClose} size={ModalSize.Medium}>
            <ModalContent>
                <div className="complete-work-order-modal">
                    <ModalHeader>
                        <ModalHeaderTitle>{title}</ModalHeaderTitle>
                    </ModalHeader>
                    <ModalBody>
                        <Grid columns={1}>
                            <FormField label="Completion Date">
                                <TextInputDateTimePicker
                                    value={completionDate}
                                    onChange={(date) => date && setCompletionDate(date)}
                                    disableFuture={true}
                                    disablePast={false}
                                />
                                {!completionDate && (
                                    <FormFieldNote type={FormFieldNoteType.Error}>
                                        Service Date and Time required
                                    </FormFieldNote>
                                )}
                                {completionDate && completionDate > new Date() ? (
                                    <FormFieldNote type={FormFieldNoteType.Error}>
                                        Service time cannot be in the future
                                    </FormFieldNote>
                                ) : (
                                    ""
                                )}
                                <FormFieldNote type={FormFieldNoteType.Info}>
                                    The date when all work was completed. Defaults to today&apos;s date.
                                </FormFieldNote>
                            </FormField>

                            {additionalFields}

                            <FormField label={
                                <div className="service-items-label-with-check-all">
                                    <span>Service Items Review</span>
                                    {serviceItems.length > 0 && (
                                        <span className="completed-title">Completed</span>
                                    )}
                                </div>
                            }>
                                {serviceItems.length === 0 ? (
                                    <div className="no-items">
                                        <img src={ImageUrls.Info} alt="warning" />
                                        <p>No service items added to this work order</p>
                                    </div>
                                ) : (
                                    <div className="service-items-review scrollable">
                                        <div className="check-all-row">
                                            <label className="check-all-label">
                                                <span>Check All</span>
                                                <input
                                                    type="checkbox"
                                                    checked={allCompleted}
                                                    onChange={toggleAllItems}
                                                    aria-label="Check all items"
                                                />
                                            </label>
                                        </div>
                                        {serviceItems.map((item) => {
                                            const missingFields = missingFieldsFor(item.uiElementId)
                                            const hasMissingFields = missingFields.length > 0
                                            const isDone = itemsStatus[item.uiElementId] === WorkRequestStatus.Done
                                            // Completing is blocked, un-completing is not: an item
                                            // finished before the requirement existed stays editable.
                                            const completionBlocked = hasMissingFields && !isDone

                                            return (
                                                <div
                                                    key={item.uiElementId}
                                                    className={`service-item-card ${isDone ? "completed" : ""} ${hasMissingFields ? "missing-required-fields" : ""}`}
                                                >
                                                    <div className="service-item-name">
                                                        {item.name || `Service ${item.uiElementId}`}
                                                        {hasMissingFields && (
                                                            <div className="missing-required-fields-note">
                                                                <img src={ImageUrls.Info} alt="" />
                                                                <span>
                                                                    Missing required{" "}
                                                                    {missingFields.length === 1 ? "field" : "fields"}:{" "}
                                                                    {labelsForServiceItemRequiredFields(
                                                                        missingFields
                                                                    ).join(", ")}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="completed-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={isDone}
                                                            disabled={completionBlocked}
                                                            title={
                                                                completionBlocked
                                                                    ? "Fill in the required fields on this Service Item before completing it."
                                                                    : undefined
                                                            }
                                                            onChange={() => toggleItem(item.uiElementId)}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </FormField>
                        </Grid>
                    </ModalBody>
                    <ModalButtons>
                        <Button label="Cancel" type={ButtonType.Text} onClick={onClose} />
                        <Button
                            label={confirmLabel}
                            type={ButtonType.Contained}
                            theme={ButtonTheme.Dark}
                            isEnabled={allCompleted && dateIsValid() && additionalFieldsValid}
                            onClick={() =>
                                onConfirm(
                                    completionDate,
                                    serviceItems.map((i) => ({
                                        ...i,
                                        status: itemsStatus[i.uiElementId],
                                    }))
                                )
                            }
                        />
                    </ModalButtons>
                </div>
            </ModalContent>
        </Modal>
    )
}
